import {
  Component,
  Prop,
  State,
  Watch,
  Event,
  EventEmitter,
  Element,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

@Component({
  tag: 'ui-in-view-watcher',
  styleUrl: 'ui-in-view-watcher.scss',
  shadow: false,
})
export class UiInViewWatcher {
  @Element() el: HTMLElement;

  /**
    This property will change when at least
    one watched element is in view (intersecting with the screen)
  */
  @Prop({
    mutable: true,
    reflect: true,
  })
  active = false;

  /**
    This property takes an element selector which defines, which elements should be watched for
  */
  @Prop() watch: string;

  /**
   * Margin around the root.
   * Can have values similar to the CSS margin property,
   * e.g. "10px 20px 30px 40px" (top, right, bottom, left).
   * The values can be percentages.
   * This set of values serves to grow or shrink each side of the root element's bounding box
   * before computing intersections.
   *  Defaults to all zeros.
   */
  @Prop() rootMargin = '0px';

  @State() watchedElements: Map<Element, boolean> = new Map();

  intersectionObserver: IntersectionObserver;

  mutationObserver = new MutationObserver(([mutation]) => {
    const addedNodes: Node[] = [].slice.call(mutation.addedNodes);
    const matchedElements = addedNodes
      .filter((node) => node.nodeType === 1)
      .filter((elementNode: Element) => elementNode.matches(this.watch));
    if (matchedElements.length > 0) {
      this.watchElements();
    }
  });

  @Event({ eventName: 'uiInViewWatcherActiveChange' })
  activeChange: EventEmitter<boolean>;

  @Watch('active')
  onActiveChanged(currentValue: boolean) {
    this.activeChange.emit(currentValue);
  }

  @Watch('rootMargin')
  initIntersectionObserver() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = new IntersectionObserver(
      ([e]) => {
        this.watchedElements.set(e.target, e.isIntersecting);
        this.active = [...this.watchedElements.values()].some(
          (active) => active
        );
      },
      {
        threshold: [0.01],
        rootMargin: this.rootMargin,
      }
    );
    this.watchForDomChanges();
    this.watchElements();
  }

  componentWillLoad() {
    this.initIntersectionObserver();
  }

  disconnectedCallback() {
    this.intersectionObserver.disconnect();
    this.mutationObserver.disconnect();
  }

  @Watch('watch')
  watchElements() {
    const newWatchedElements = new Map();
    document.querySelectorAll(this.watch).forEach((element) => {
      newWatchedElements.set(
        element,
        this.watchedElements.get(element) || false
      );
    });
    this.watchedElements = newWatchedElements;
  }

  @Watch('watchedElements')
  onWatchedElementsChange(
    newValue: Map<Element, boolean>,
    oldValue: Map<Element, boolean>
  ) {
    if (oldValue) {
      [...oldValue.keys()].forEach((e) =>
        this.intersectionObserver.unobserve(e)
      );
    }
    if (newValue) {
      [...newValue.keys()].forEach((e) => this.intersectionObserver.observe(e));
    }
  }

  watchForDomChanges() {
    this.mutationObserver.observe(document.body, {
      subtree: true,
      childList: true,
    });
  }
}

versionComponent(UiInViewWatcher);
